import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthPersistState } from "@/store/auth/auth.store";
import CommunityActionButtons from "./helperfunction/CommunityActionButton";
import { siteRoutes } from "@/routes/siteRoutesList";
import { createToast } from "@/utils/toaster/createToast";
import { Avatar, Box, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { CreatorCardProps } from "./CreatorCard.types";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import i18next from "i18next";

/**
 * CreatorCard component displays a card with information about a content creator.
 * It includes the creator's avatar, username, full name, and follower count.
 * It also provides action buttons for following/unfollowing the creator.
 *
 * @prop {ChallengeCreatorProfile} creatorProfile - The profile information of the creator.
 * @prop {number} creatorFollowersNumber - The number of followers the creator has.
 * @prop {boolean} isFollowedByCurrentUser - Indicates if the current user follows the creator.
 *
 * @returns {JSX.Element} The rendered CreatorCard component.
 */

const CreatorCard: FC<CreatorCardProps> = (props): JSX.Element => {
  const navigate = useNavigate();
  const user = useAuthPersistState((s) => s.user);
  const currentUserId = user?.uid;

  const [creatorFollowers, setCreatorFollowers] = useState(
    props.creatorFollowersNumber
  );

  useEffect(() => {
    setCreatorFollowers(props.creatorFollowersNumber);
  }, [props.creatorFollowersNumber]);

  const handleLinkClick = useCallback(() => {
    if (!currentUserId) {
      navigate(siteRoutes["login"]);
      createToast({
        type: "error",
        message: "You need to be logged to go here",
      });
    }

    navigate(`${siteRoutes["profileAndSlug"]}${props.creatorProfile.slug}`);
  }, [currentUserId, props.creatorProfile.slug]);

  // Variables
  const userName = props.creatorProfile.userName;
  const fullName = props.creatorProfile.fullName;
  const avatarUrl = props.creatorProfile.profileLogoUrl;
  const avatarFallback = userName.slice(0, 1);

  const brandHasFriends = brand.hasFeature.friends;

  if (props.zone) {
    return (
      <Box>
        <Heading as="h3" size="3" mb="1">
          {i18next.t("challenge.creator.headline")}
        </Heading>

        {/* Creator card */}
        <Card
          onClick={() => navigate(`${siteRoutes["zone"]}${props.zone.slug}`)}
        >
          <Flex align="center" gap="3">
            {/* Avatar */}
            <Box>
              <Avatar
                src={props.zone.imageURL}
                fallback={avatarFallback}
                radius="full"
                size="5"
              />
            </Box>

            <Flex direction="column" gap="0">
              {/* Username */}
              {props.zone.title && (
                <Heading as="h4" size="3" mb="0">
                  {props.zone.title}
                </Heading>
              )}

              {/* Full name */}
              {props.zone.tagline && (
                <Text size="2" color="gray">
                  {props.zone.tagline}
                </Text>
              )}
            </Flex>
          </Flex>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Heading as="h3" size="3" mb="1">
        {i18next.t("challenge.creator.headline")}
      </Heading>

      {/* Creator card */}
      <Card onClick={handleLinkClick}>
        <Flex align="center" gap="3">
          {/* Avatar */}
          <Box>
            <Avatar
              src={avatarUrl}
              fallback={avatarFallback}
              radius="full"
              size="5"
            />
          </Box>

          <Flex direction="column" gap="0">
            {/* Username */}
            {userName && (
              <Heading as="h4" size="3" mb="0">
                {userName}
              </Heading>
            )}

            {/* Full name */}
            {fullName && (
              <Text size="2" color="gray">
                {fullName}
              </Text>
            )}

            {/* Followers */}
            {creatorFollowers > 0 && brandHasFriends && (
              <Flex gap="1">
                <Text size="2" color="gray">
                  {creatorFollowers} followers
                </Text>
              </Flex>
            )}
          </Flex>

          {/* Action buttons */}
          {currentUserId !== props.creatorProfile.uid && brandHasFriends && (
            <Box ml="auto">
              <CommunityActionButtons
                isFollowedByCurrentUser={props.isFollowedByCurrentUser}
                uid={props.creatorProfile.uid}
              />
            </Box>
          )}
        </Flex>
      </Card>
    </Box>
  );
};

export default CreatorCard;
